/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { Loader2 } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { LeadFields, leadBodyFromForm } from "../../../components/LeadFields";
import { useCrmMeta, formForNewProject } from "./customerForm";
import { T } from "../../../lib/i18n";
import { ProjectAddressCheck, useProjectAddressCheck } from "../../../components/ProjectAddressCheck";

/* 先建 CRM 客户和营销方案，正式设计项目由签约及首款确认后流转。 */
export const NewProjectDialog = ({ meta, api, branch, onClose, onCreated }) => {
  const { lang } = useApp();
  const zh = lang === "zh";
  const crm = useCrmMeta();
  const [form, setForm] = useState(null);
  const addressCheck = useProjectAddressCheck(api, form?.address, form?.city);
  const [extra, setExtra] = useState({ note: "", approvalMode: "auto" });
  const [cityMode, setCityMode] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (crm && !form) setForm(formForNewProject(crm, branch)); }, [crm]); // eslint-disable-line
  const city = (form?.city || "").trim();
  useEffect(() => {
    if (!city) { setCityMode(null); return; }
    const t = setTimeout(() => api.get("/design/v3/settings/city-modes/lookup", { params: { city } }).then(({ data }) => setCityMode(data.mode ? data : { city, mode: null })).catch(() => setCityMode(null)), 350);
    return () => clearTimeout(t);
  }, [city]); // eslint-disable-line
  const submit = async () => {
    if (!form.firstName.trim() && !form.lastName.trim()) return toast.error(zh ? "请填写客户姓名" : "Name required");
    if (!form.branch) return toast.error(zh ? "请选择所属分公司" : "Branch required");
    if (!form.address.trim()) return toast.error(T("请填写项目地址"));
    setBusy(true);
    try {
      const customer = leadBodyFromForm(form);
      const addressAcknowledgment = await addressCheck.check(customer, "design");
      if (!addressAcknowledgment) { setBusy(false); return; }
      const { data } = await api.post("/design/v3/projects", { customer, addressAcknowledgment, note: extra.note, approvalMode: extra.approvalMode });
      toast.success(T("已创建客户及签约前营销方案 {0}，签约并确认首款后流转至项目管理", [data.projectId]));
      onCreated(data);
    } catch (e) { if (!addressCheck.handleError(e)) toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="dv3-new-dialog">
        <DialogHeader>
          <DialogTitle>{T("新建客户与营销方案")}</DialogTitle>
          <DialogDescription>{T("先在 CRM 新建客户及签约前营销方案。销售确认合同附件和类型、财务确认首款后，项目才按合同范围流转。")}</DialogDescription>
        </DialogHeader>
        <ProjectAddressCheck state={addressCheck} onLeave={onClose} />
        {!form || !crm ? <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : <>
          <LeadFields form={form} setForm={setForm} meta={crm} zh={zh} tid="dv3-new" />
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="dv3-new-design-extra">
            <label className="block text-xs text-slate-600">{T("起始工作状态")}
              <select data-testid="dv3-new-workStatus" value="pre_sign_concept" disabled className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white"><option value="pre_sign_concept">{T("签约前·营销方案")}</option></select>
            </label>
            <label className="block text-xs text-slate-600">{T("设计部备注")}<input data-testid="dv3-new-note" value={extra.note} onChange={(e) => setExtra({ ...extra, note: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white" /></label>
            <label className="block text-xs text-slate-600 col-span-2">{T("审批模式（用于后续签约后的设计流程）")}
              <select data-testid="dv3-new-approvalMode" value={extra.approvalMode} onChange={(e) => setExtra({ ...extra, approvalMode: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white">
                <option value="auto">{cityMode?.mode ? T("按城市默认：{0}（{1}）", [cityMode.mode === "concurrent" ? T("同步提交") : T("分开提交"), cityMode.city]) : T("按城市默认（未设置该城市 → 分开提交）")}</option>
                <option value="sequential">{T("分开提交：先 Planning，批准后再 Building")}</option>
                <option value="concurrent">{T("同步提交：Planning + Building 同时提交、同步审查（阶段显示合并，任务仍需全部完成）")}</option>
              </select>
              <span className={`mt-1 inline-block text-[10px] ${cityMode?.mode === "concurrent" ? "text-teal-700" : "text-slate-400"}`} data-testid="dv3-new-approvalMode-hint" data-city-mode={cityMode?.mode || ""}>{city ? (cityMode?.mode ? T("{0} 已设置默认「{1}」，新项目将自动采用；可在此覆盖", [cityMode.city, cityMode.mode === "concurrent" ? T("同步提交") : T("分开提交")]) : T("{0} 未设置城市默认（admin 可在「集成与设置 → 城市默认审批模式」配置）", [city])) : T("填写城市后自动匹配该城市默认审批模式")}</span>
            </label>
          </div>
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" data-testid="dv3-new-cancel" onClick={onClose}>{T("取消")}</Button>
            <Button size="sm" data-testid="dv3-new-submit" onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : T("创建")}</Button>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};
